/**
 * Settlement Audit Service
 *
 * Fetches live on-ledger settlement data from the KRO Marketplace API for the
 * Trust Tech Studio Settlement Hub audit track. Settlement is on-ledger CIP-56
 * atomic DvP on the Canton Global Synchronizer — there is no off-ledger message
 * archive to read.
 */

// API base is shared with KRO Marketplace (same backend, same Azure SQL database).
// Dev: set REACT_APP_KRO_API_BASE=http://localhost:5000/api/v1 in .env.development
// Prod: https://ca-api-prod-wus2.reddesert-84090673.westus2.azurecontainerapps.io/api/v1
const _apiRoot = process.env.REACT_APP_KRO_API_BASE
  || 'https://ca-api-prod-wus2.reddesert-84090673.westus2.azurecontainerapps.io/api/v1';

// Strip trailing /api/v1 prefix when building full-path URLs in this service.
const API_BASE = _apiRoot.replace(/\/api\/v1\/?$/, '');

// SECURITY: no privileged credential is sent from the browser. The
// ippo-settlement / marker-assignments routes are gated by requireSettlementAuth
// (server-to-server X-Admin-Key or a per-user Bearer JWT). The studio no longer
// embeds an admin key — those internal audit panels degrade gracefully (the UI
// shows a "service unavailable" state) until a per-user session is wired up.
// Do NOT reintroduce a static admin key into the client bundle.

/**
 * Generic fetch with timeout, CORS-safe error handling, and graceful degradation.
 * Returns { data, status, ok } so callers can distinguish 500 (server error)
 * from 200 (success) or 404 (not found) without throwing.
 */
async function apiFetch(url, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const res = await fetch(url, {
      ...options,
      headers: { ...(options.headers || {}) },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) {
      console.warn(`[SettlementAudit] HTTP ${res.status} from ${url}`);
      return { data: null, status: res.status, ok: false };
    }
    const data = await res.json();
    return { data, status: res.status, ok: true };
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') {
      console.warn(`[SettlementAudit] Timeout fetching ${url}`);
      return { data: null, status: 408, ok: false };
    }
    console.warn(`[SettlementAudit] Network/CORS error fetching ${url}:`, err.message);
    return { data: null, status: 0, ok: false };
  }
}

// ─── IPPO Settlement ──────────────────────────────────────────────────────────

export async function fetchIppoLog(limit = 50) {
  const res = await apiFetch(`${API_BASE}/api/v1/ippo-settlement/log?limit=${limit}`);
  return { data: res.data?.data ?? [], status: res.status, ok: res.ok };
}

export async function fetchIppoSummary() {
  const res = await apiFetch(`${API_BASE}/api/v1/ippo-settlement/summary`);
  return { data: res.data?.data ?? null, status: res.status, ok: res.ok };
}

export async function fetchIppoHealth() {
  const res = await apiFetch(`${API_BASE}/api/v1/ippo-settlement/health`);
  return { data: res.data?.data ?? null, status: res.status, ok: res.ok };
}

// ─── Alchemy Settlement ───────────────────────────────────────────────────────

export async function fetchDeposits(filters = {}) {
  const params = new URLSearchParams();
  if (filters.token)     params.set('token',     filters.token);
  if (filters.status)    params.set('status',    filters.status);
  if (filters.unmatched) params.set('unmatched', 'true');
  const qs = params.toString();
  const res = await apiFetch(
    `${API_BASE}/api/v1/alchemy-settlement/deposits${qs ? '?' + qs : ''}`
  );
  return { data: res.data?.data ?? [], status: res.status, ok: res.ok };
}

export async function fetchMonitorStatus() {
  const res = await apiFetch(`${API_BASE}/api/v1/alchemy-settlement/monitor/status`);
  return { data: res.data?.data ?? null, status: res.status, ok: res.ok };
}

export async function fetchAlchemyHealth() {
  const res = await apiFetch(`${API_BASE}/api/v1/alchemy-settlement/health`);
  return { data: res.data?.data ?? null, status: res.status, ok: res.ok };
}

// ─── Composite fetch (all sources at once) ────────────────────────────────────

export async function fetchAllAuditData() {
  const [ippoLog, ippoSummary, deposits, monitorStatus] = await Promise.allSettled([
    fetchIppoLog(100),
    fetchIppoSummary(),
    fetchDeposits(),
    fetchMonitorStatus(),
  ]);

  const ippoLogVal       = ippoLog.status       === 'fulfilled' ? ippoLog.value       : { data: [], status: 0, ok: false };
  const ippoSummaryVal   = ippoSummary.status   === 'fulfilled' ? ippoSummary.value   : { data: null, status: 0, ok: false };
  const depositsVal      = deposits.status      === 'fulfilled' ? deposits.value      : { data: [], status: 0, ok: false };
  const monitorStatusVal = monitorStatus.status === 'fulfilled' ? monitorStatus.value : { data: null, status: 0, ok: false };

  // Detect whether the backend API container is broadly unavailable (all endpoints 500).
  const apiStatuses = [ippoLogVal.status, ippoSummaryVal.status, depositsVal.status, monitorStatusVal.status];
  const serviceDown = apiStatuses.every(s => s === 500 || s === 0 || s === 408);
  const anyApiSuccess = apiStatuses.some(s => s === 200);

  return {
    ippoLog:       ippoLogVal.data,
    ippoSummary:   ippoSummaryVal.data,
    deposits:      depositsVal.data,
    monitorStatus: monitorStatusVal.data,
    serviceDown,
    anyApiSuccess,
    apiStatuses: {
      ippoLog:       ippoLogVal.status,
      ippoSummary:   ippoSummaryVal.status,
      deposits:      depositsVal.status,
      monitorStatus: monitorStatusVal.status,
    },
    errors: {
      ippoLog:       !ippoLogVal.ok       ? `HTTP ${ippoLogVal.status}`       : null,
      ippoSummary:   !ippoSummaryVal.ok   ? `HTTP ${ippoSummaryVal.status}`   : null,
      deposits:      !depositsVal.ok      ? `HTTP ${depositsVal.status}`      : null,
      monitorStatus: !monitorStatusVal.ok ? `HTTP ${monitorStatusVal.status}` : null,
    },
  };
}
